import { z } from "zod";

const MAX_NAME_LENGTH = 100;

type Address = {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
};

type TaxId = {
  country: string;
  type: string;
  value: string;
};

type AccountingProvider = {
  provider_type: string;
  external_provider_id: string;
};

type AccountingSyncConfiguration = {
  excluded: boolean;
  accounting_providers: AccountingProvider[];
};

type ReportingConfiguration = {
  exempt: boolean;
};

type CustomerHierarchyNode = {
  id: string;
  external_customer_id: string;
};

type CustomerHierarchy = {
  parent: CustomerHierarchyNode | null;
  children: CustomerHierarchyNode[];
};

type Customer = {
  id: string;
  external_customer_id: string;
  name: string;
  email: string;
  timezone: string;
  payment_provider: string;
  payment_provider_id: string;
  created_at: string;
  balance: string;
  currency: string;
  auto_collection: boolean;
  email_delivery: boolean;
  portal_url: string;
  accounting_sync_configuration: AccountingSyncConfiguration;
  additional_emails: string[];
  billing_address: Address | null;
  shipping_address: Address | null;
  tax_id: TaxId | null;
  exempt_from_automated_tax: boolean;
  reporting_configuration: ReportingConfiguration | null;
  hierarchy: CustomerHierarchy;
  metadata: Record<string, unknown>;
};

type BillableMetric = {
  id: string;
};

type Item = {
  id: string;
  name: string;
};

type CustomExpiration = {
  duration: number;
  duration_unit: string;
};

type CreditAllocation = {
  currency: string;
  allows_rollover: boolean;
  custom_expiration: CustomExpiration | null;
};

type UnitConfig = {
  unit_amount: string;
  scaling_factor?: number | null;
};

type ConversionRateConfig = {
  conversion_rate_type: string;
  unit_config: UnitConfig;
};

type BillingCycleConfiguration = {
  duration: number;
  duration_unit: string;
};

type DimensionalPriceConfiguration = {
  dimensional_price_group_id: string;
  dimension_values: string[];
};

type Filter = {
  field: string;
  operator: string;
  values: string[];
};

type Discount = {
  discount_type: string;
  applies_to_price_ids: string[];
  filters: Filter[];
  reason: string;
  percentage_discount?: number;
  amount_discount?: string;
};

type MinimumMaximum = {
  minimum_amount?: string;
  maximum_amount?: string;
  filters: Filter[];
  applies_to_price_ids: string[];
};

export type Price = {
  id: string;
  name: string;
  external_price_id: string | null;
  price_type: "fixed_price" | "usage_price";
  model_type: string;
  created_at: string;
  cadence: string;
  billing_cycle_configuration: BillingCycleConfiguration;
  invoicing_cycle_configuration: BillingCycleConfiguration | null;
  billable_metric: BillableMetric | null;
  dimensional_price_configuration: DimensionalPriceConfiguration | null;
  fixed_price_quantity: number | null;
  currency: string;
  conversion_rate: number | null;
  conversion_rate_config: ConversionRateConfig | null;
  item: Item;
  credit_allocation: CreditAllocation | null;
  discount: Discount | null;
  minimum: MinimumMaximum | null;
  maximum: MinimumMaximum | null;
  minimum_amount: string | null;
  maximum_amount: string | null;
  unit_config: UnitConfig;
  plan_phase_order: number | null;
  replaces_price_id: string | null;
  metadata: Record<string, unknown>;
};

type Product = {
  id: string;
  name: string;
  created_at: string;
};

type TrialConfig = {
  trial_period: number | null;
  trial_period_unit: string;
};

type BasePlan = {
  id: string;
  external_plan_id: string;
  name: string;
};

type PlanPhase = {
  id: string;
  description: string;
  duration: number;
  duration_unit: string;
  name: string;
  order: number;
  minimum: MinimumMaximum | null;
  maximum: MinimumMaximum | null;
  maximum_amount: string | null;
  minimum_amount: string | null;
  discount: Discount | null;
};

type Adjustment = {
  id: string;
  is_invoice_level: boolean;
  filters: Filter[];
  applies_to_price_ids: string[];
  reason: string;
  adjustment_type: string;
  usage_discount: number;
  plan_phase_order: number;
};

type Plan = {
  id: string;
  name: string;
  description: string;
  external_plan_id: string | null;
  created_at: string;
  status: string;
  currency: string;
  invoicing_currency: string;
  maximum_amount: string | null;
  minimum_amount: string | null;
  net_terms: number;
  default_invoice_memo: string | null;
  adjustments: Adjustment[];
  base_plan: BasePlan | null;
  base_plan_id: string | null;
  discount: Discount | null;
  maximum: MinimumMaximum | null;
  minimum: MinimumMaximum | null;
  plan_phases: PlanPhase[];
  prices: Price[];
  product: Product;
  trial_config: TrialConfig;
  version: number;
  metadata: Record<string, unknown>;
};

type TrialInfo = {
  end_date: string | null;
  maximum_intervals: MaximumInterval[];
  pending_subscription_change: PendingSubscriptionChange | null;
  metadata: Record<string, unknown>;
};

type BillingCycleAnchorConfiguration = {
  day: number;
  month: number | null;
  year: number | null;
};

type FixedFeeQuantitySchedule = {
  price_id: string;
  start_date: string;
  end_date: string | null;
  quantity: number;
};

type RedeemedCoupon = {
  coupon_id: string;
  start_date: string;
  end_date: string;
};

type FixedFeeQuantityTransition = {
  price_id: string;
  effective_date: string;
  quantity: number;
};

type PriceInterval = {
  id: string;
  start_date: string;
  end_date: string | null;
  price: Price;
  billing_cycle_day: number;
  fixed_fee_quantity_transitions: FixedFeeQuantityTransition[] | null;
  current_billing_period_start_date: string;
  current_billing_period_end_date: string;
  filter: string | null;
  usage_customer_ids: string[] | null;
};

type AdjustmentInterval = {
  id: string;
  adjustment: Adjustment;
  start_date: string;
  end_date: string;
  applies_to_price_interval_ids: string[];
};

type DiscountInterval = {
  discount_type: string;
  amount_discount: string | null;
  percentage_discount?: number;
  start_date: string;
  end_date: string;
  filters: Filter[];
  applies_to_price_interval_ids: string[];
};

type MinimumInterval = {
  start_date: string;
  end_date: string;
  filters: Filter[];
  applies_to_price_interval_ids: string[];
  minimum_amount: string;
};

type MaximumInterval = {
  start_date: string;
  end_date: string;
  filters: Filter[];
  applies_to_price_interval_ids: string[];
  maximum_amount: string;
};

type PendingSubscriptionChange = {
  id: string;
};

export type SubscriptionResponse = {
  id: string;
  customer: Customer;
  plan: Plan;
  name: string;
  start_date: string;
  end_date: string | null;
  created_at: string;
  current_billing_period_start_date: string;
  current_billing_period_end_date: string;
  status: "active" | "ended" | "upcoming";
  trial_info: TrialInfo;
  active_plan_phase_order: number | null;
  fixed_fee_quantity_schedule: FixedFeeQuantitySchedule[];
  default_invoice_memo: string | null;
  auto_collection: boolean;
  net_terms: number;
  redeemed_coupon: RedeemedCoupon | null;
  billing_cycle_day: number;
  billing_cycle_anchor_configuration: BillingCycleAnchorConfiguration;
  invoicing_threshold: string | null;
  price_intervals: PriceInterval[];
  adjustment_intervals: AdjustmentInterval[];
  discount_intervals: DiscountInterval[];
  minimum_intervals: MinimumInterval[];
  maximum_intervals: MaximumInterval[];
  pending_subscription_change: PendingSubscriptionChange | null;
  metadata: Record<string, unknown>;
};

type ScheduledChange = {
  created_at: string;
  end_date: string | null;
  plan: {
    external_plan_id: string;
    id: string;
    name: string;
  };
  start_date: string;
};

export type ScheduledChangesResponse = ScheduledChange[];

// Customer update schema and types
export const updateCustomerSchema = z.object({
  name: z
    .string()
    .min(1, "Name is required")
    .max(
      MAX_NAME_LENGTH,
      `Name must be less than ${MAX_NAME_LENGTH} characters`
    ),
});

export type UpdateCustomer = z.infer<typeof updateCustomerSchema>;

type CustomerData = {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
  stripe_customer_id: string;
  orb_customer_id: string;
  plan_id: string;
  subscription_id: string;
};

export type CustomerResponse = {
  data: CustomerData;
  code: number;
  msg: string;
};

export type PlanName = "free" | "starter" | "growth" | "enterprise";

// Usage types
type UsageSample = {
  quantity: number;
  timeframe_end: string;
  timeframe_start: string;
};

type BillableMetricInfo = {
  id: string;
  name: string;
};

type MetricGroup = {
  property_key: string;
  property_value: string;
};

export type MetricUsage = {
  billable_metric: BillableMetricInfo;
  usage: UsageSample[];
  view_mode: "cumulative" | "periodic";
  metric_group?: MetricGroup;
};

export type UsageResponse = MetricUsage[];

type AutoCollection = {
  enabled: boolean;
  next_attempt_at: string | null;
  num_attempts: number;
  previously_attempted_at: string | null;
};

type InvoiceCustomer = {
  id: string;
  external_customer_id: string;
};

type InvoiceLineItemPrice = {
  id: string;
  billable_metric: {
    id: string;
  };
  billing_cycle_configuration: {
    duration: number;
    duration_unit: string;
  };
  cadence: string;
  conversion_rate: number;
  conversion_rate_config: {
    conversion_rate_type: string;
    unit_config: {
      unit_amount: string;
    };
  };
  created_at: string;
  credit_allocation: unknown | null;
  currency: string;
  discount: unknown | null;
  external_price_id: string | null;
  fixed_price_quantity: number | null;
  invoicing_cycle_configuration: unknown | null;
  item: {
    id: string;
    name: string;
  };
  maximum: unknown | null;
  maximum_amount: string | null;
  metadata: Record<string, unknown>;
  minimum: unknown | null;
  minimum_amount: string | null;
  model_type: string;
  name: string;
  plan_phase_order: number | null;
  price_type: string;
  replaces_price_id: string | null;
  unit_config: {
    unit_amount: string;
    scaling_factor?: number | null;
  };
  dimensional_price_configuration: unknown | null;
};

type InvoiceLineItem = {
  id: string;
  adjusted_subtotal: string;
  adjustments: unknown[];
  amount: string;
  credits_applied: string;
  discount: unknown | null;
  end_date: string;
  filter: unknown | null;
  grouping: unknown | null;
  maximum: unknown | null;
  maximum_amount: string | null;
  minimum: unknown | null;
  minimum_amount: string | null;
  name: string;
  partially_invoiced_amount: string;
  price: InvoiceLineItemPrice;
  quantity: number;
  start_date: string;
  sub_line_items: unknown[];
  subtotal: string;
  tax_amounts: unknown[];
  usage_customer_ids: string[] | null;
};

type InvoiceSubscription = {
  id: string;
};

export type Invoice = {
  id: string;
  amount_due: string;
  auto_collection: AutoCollection;
  billing_address: Address | null;
  created_at: string;
  credit_notes: unknown[];
  currency: string;
  customer: InvoiceCustomer;
  customer_balance_transactions: unknown[];
  customer_tax_id: TaxId | null;
  discount: unknown | null;
  discounts: unknown[];
  due_date: string | null;
  eligible_to_issue_at: string | null;
  hosted_invoice_url: string;
  invoice_date: string;
  invoice_number: string;
  invoice_pdf: string | null;
  invoice_source: string;
  issue_failed_at: string | null;
  issued_at: string | null;
  line_items: InvoiceLineItem[];
  maximum: unknown | null;
  maximum_amount: string | null;
  memo: string | null;
  metadata: Record<string, unknown>;
  minimum: unknown | null;
  minimum_amount: string | null;
  paid_at: string | null;
  payment_attempts: unknown[];
  payment_failed_at: string | null;
  payment_started_at: string | null;
  scheduled_issue_at: string | null;
  shipping_address: Address | null;
  status: "paid" | "unpaid" | "void" | "draft" | "issued" | "synced";
  subscription: InvoiceSubscription;
  subtotal: string;
  sync_failed_at: string | null;
  target_date?: string;
  total: string;
  voided_at: string | null;
  will_auto_issue: boolean;
};

export type RecentInvoicesResponse = Invoice[];

export type UpcomingInvoiceResponse = Invoice;

// Checkout API types
export type CheckoutRequest = {
  plan_id: string;
};

type CheckoutSessionData = {
  stripe_customer_session_secret: string;
  stripe_payment_intent_secret: string;
  stripe_setup_intent_secret: string;
  expires_at: string;
};

export type CheckoutResponse = {
  code: number;
  msg: string;
  data: CheckoutSessionData;
};
